import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import { CombatProShell, cps } from './CombatProShared';
import { glassSurface } from './config/glassTheme';
import { generatorPageSizeForWidth, selectGeneratorGrid } from './config/generatorCatalogueView';
import GeneratorReselectionModal from './GeneratorReselectionModal';
import {
  ALL_GROUPS, groupFromSearch, groupIdentity, groupRoute, isPrimaryMember, itemInGroup, orderForGroup, serverGroups,
} from './config/generatorExperienceGroups';

const GENERATORS_ROUTE = '/dashboard/standalone-generators';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

export function generatorCommerceIntent(search) {
  const params = new URLSearchParams(search || '');
  const slug = String(params.get('generator') || '');
  const action = params.get('action') === 'buy' ? 'buy' : (params.get('view') === 'info' ? 'info' : null);
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && action ? { slug, action } : null;
}

// Card artwork is a frontend-owned static asset. Older deployed API revisions do not expose
// artwork_path for every catalogue row, so derive the canonical path from the stable product slug
// while still honoring an explicitly authored server path when one is present.
function withCanonicalArtwork(item) {
  return {
    ...item,
    artwork_path: item.artwork_path || `/generator-art/${item.slug}.png`,
  };
}

// View-state key: preserves the customer's catalogue filters and page within this browser tab.
// Pagination page size, search, sort and filtering all live in config/generatorCatalogueView.js.
const SG_STATE_KEY = 'vi-standalone-generators-view';

// Restore the catalogue view (search / category / sort / filters / page). sessionStorage is
// per-tab and non-fatal if unavailable.
function readSavedView() {
  try { return JSON.parse(sessionStorage.getItem(SG_STATE_KEY)) || {}; } catch { return {}; }
}

// THIS is the VISINVOKE Dashboard's generator surface. Detailed generator information and
// commercial presentation live here: descriptions, capability points, individual pricing, the
// choose-any pack ladder, savings and the selection/checkout flow.
//
// The DCC is a catalogue/access/launch surface and shows its own established product cards. It
// deliberately renders none of this. The two surfaces share the server catalogue as DATA authority;
// they do not share presentation.
/**
 * The approved filter taxonomy, in the approved order. The catalogue supplies the categories; this
 * only fixes the ORDER a customer reads them in, so a pill row never reshuffles itself when the
 * catalogue grows.
 */
const CATEGORY_ORDER = [
  'DM & SESSION', 'CAMPAIGN & STORY', 'NPC & SOCIAL', 'WORLD & SETTLEMENT',
  'PLAYER & PROGRESSION', 'ITEMS & COMMERCE', 'RULES & RESOLUTION',
];

/**
 * Products given the deeper featured treatment above the normal grid, in display order.
 *
 * This is a PRESENTATION decision, not a commercial one: the price, the entitlement and the
 * capability points still come from the catalogue like every other generator, and a slug listed
 * here that the catalogue does not carry is simply skipped rather than invented.
 *
 * Featured placement is permanent and independent of entitlement. Professionals & Trades holds
 * the right slot as an active product; price and entitlement still come from the catalogue.
 */
const FEATURED_SLUGS = ['shop-merchant-generator', 'professionals-trades'];

/**
 * Layout that inline styles cannot express: the exact desktop column count, and the fixed card
 * zones that keep every card the same height with its sections on the same lines across every row.
 *
 * The zones are FIXED rather than content-sized on purpose. A generator with a longer purpose line
 * or a longer capability point must not push its own price and action button out of line with the
 * card beside it — the grid rows and the clamps below are what guarantee that.
 */
const SG_CSS = `
  .sg-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 14px; }
  @media (max-width: 1500px) { .sg-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
  @media (max-width: 1180px) { .sg-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  @media (max-width: 860px)  { .sg-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 600px)  { .sg-grid { grid-template-columns: 1fr; } }

  /* Three generator experiences (plus the complete collection) as the first navigation layer. */
  .sg-group-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 0 0 14px; }
  .sg-group-tab { min-height: 64px; padding: 10px 16px; border-radius: 16px; color: #fff; cursor: pointer; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: 3px; text-align: left; box-sizing: border-box; width: 100%; }
  .sg-group-tab strong { font-size: 13px; letter-spacing: .06em; text-transform: uppercase; }
  .sg-group-tab span { color: rgba(237,241,248,.70); font-size: 11.5px; font-weight: 700; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sg-group-tab:focus-visible { outline: 2px solid #f59e0b; outline-offset: 3px; }
  @media (max-width: 1100px) { .sg-group-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 560px)  { .sg-group-row { grid-template-columns: 1fr; gap: 8px; } }

  .sg-control-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin: 2px 0 20px; }
  .sg-control-launch { min-height: 70px; padding: 12px 18px; border-radius: 999px; color: #fff; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; text-align: center; }
  .sg-control-launch strong { font-size: 13px; letter-spacing: .09em; }
  .sg-control-launch span { color: rgba(237,241,248,.66); font-size: 11.5px; font-weight: 700; white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
  .sg-control-launch:hover { transform: translateY(-1px); }
  .sg-control-launch:focus-visible { outline: 2px solid #f59e0b; outline-offset: 3px; }
  @media (max-width: 760px) { .sg-control-row { grid-template-columns: 1fr; gap: 9px; } .sg-control-launch { min-height: 58px; } }

  /* Two equal halves spanning the full catalogue width. */
  .sg-featured-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: stretch; }
  @media (max-width: 1000px) { .sg-featured-row { grid-template-columns: 1fr; } }
  /* Should only one featured product ever exist, it spans rather than leaving a hole beside it. */
  .sg-featured-row.sg-featured-one { grid-template-columns: 1fr; }

  /* Equal depth is fixed, not content-derived, so the two cards match whatever they each contain. */
  .sg-featured-card .sg-f-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .sg-featured-card .sg-offers { overflow: hidden; }

  /* Geometry for both card kinds is applied INLINE (see the sg style object): the shared cps.card
     sets display/flex-direction/gap inline and a class cannot override that. What stays here is the
     text bounding that keeps those zones honest. */
  .sg-name   { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .sg-purpose{ display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .sg-offers { overflow: hidden; }
  .sg-offers li { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }

  /* Primary card action. Styled entirely by class so :hover / :focus-visible / :disabled actually
     work — an inline background would win over any state rule. Geometry comes from the card's own
     zone row, so every action keeps identical placement. */
  .sg-action {
    width: 100%; height: 100%;
    border-radius: 10px;
    border: 1.5px solid rgba(148,163,184,0.9);
    background: linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0) 54%), linear-gradient(180deg, rgba(100,116,139,0.72) 0%, rgba(100,116,139,0.30) 44%, rgba(51,65,85,0.16) 74%, rgba(9,11,17,0) 100%), linear-gradient(180deg, #14171f 0%, #090b11 100%);
    box-shadow: 0 0 20px rgba(100,116,139,0.8), 0 0 46px rgba(100,116,139,0.45), inset 0 0 24px rgba(100,116,139,0.28), inset 0 1px 0 rgba(255,255,255,0.28), 0 10px 26px rgba(0,0,0,0.55);
    color: #fff;
    text-shadow: 0 0 10px rgba(148,163,184,0.9), 0 1px 4px rgba(0,0,0,0.6);
    font-size: 12.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
    cursor: pointer;
    transition: box-shadow 140ms ease, border-color 140ms ease, color 140ms ease;
  }
  .sg-action:hover:not(:disabled) { box-shadow: 0 0 30px rgba(100,116,139,1), 0 0 62px rgba(100,116,139,0.6), inset 0 0 28px rgba(100,116,139,0.4), inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 26px rgba(0,0,0,0.55); border-color: rgba(148,163,184,1); color: #fff; }
  .sg-action:focus-visible { outline: 2px solid #f59e0b; outline-offset: 2px; }
  .sg-action:disabled { cursor: default; opacity: .55; color: #97A2B4; background: rgba(255,255,255,0.03); border-color: rgba(255,255,255,0.10); box-shadow: none; text-shadow: none; }
  /* Owned/included generators execute in the Desktop Control Centre, not this website. */
  .sg-action.is-dcc { display: flex; align-items: center; justify-content: center; cursor: default; background: rgba(34,197,139,.09); border-color: rgba(75,224,168,.55); color: #8ff0ca; box-shadow: inset 0 0 18px rgba(34,197,139,.10); text-shadow: none; }
  .sg-action.is-selected { background: linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0) 54%), linear-gradient(180deg, rgba(245,158,11,0.72) 0%, rgba(245,158,11,0.30) 44%, rgba(166,109,5,0.16) 74%, rgba(9,11,17,0) 100%), linear-gradient(180deg, #14171f 0%, #090b11 100%); border-color: rgba(251,191,36,0.9); color: #fff; box-shadow: 0 0 20px rgba(245,158,11,0.8), 0 0 46px rgba(245,158,11,0.45), inset 0 0 24px rgba(245,158,11,0.28), inset 0 1px 0 rgba(255,255,255,0.28), 0 10px 26px rgba(0,0,0,0.55); text-shadow: 0 0 10px rgba(251,191,36,0.9), 0 1px 4px rgba(0,0,0,0.6); }
  .sg-action.is-selected:hover:not(:disabled) { box-shadow: 0 0 30px rgba(245,158,11,1), 0 0 62px rgba(245,158,11,0.6), inset 0 0 28px rgba(245,158,11,0.4), inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 26px rgba(0,0,0,0.55); border-color: rgba(251,191,36,1); color: #fff; }

  /* WHAT IT OFFERS: a scannable list, not default browser bullets. */
  .sg-offers ul { list-style: none; margin: 0; padding: 0; }
  .sg-offers li { position: relative; padding-left: 13px; }
  .sg-offers li::before {
    content: ''; position: absolute; left: 2px; top: 8px;
    width: 4px; height: 4px; border-radius: 50%; background: rgba(167,139,250,0.85);
  }

  .sg-search:focus { outline: none; border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,.18); }
  .sg-pill { transition: background 140ms ease, border-color 140ms ease, color 140ms ease; }
  .sg-more-info { border: 0; background: transparent; color: #c4b5fd; font: inherit; font-weight: 900; letter-spacing: .07em; text-transform: uppercase; cursor: pointer; padding: 2px 0; }
  .sg-more-info:hover { color: #fff; text-decoration: underline; text-underline-offset: 3px; }
  .sg-more-info:focus-visible { outline: 2px solid #f59e0b; outline-offset: 3px; border-radius: 3px; }
  .sg-info-backdrop, .sg-control-backdrop { position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center; padding: 24px; background: rgba(2,4,10,.82); backdrop-filter: blur(12px); }
  .sg-info-dialog { width: min(1180px, 100%); max-height: calc(100vh - 32px); overflow: auto; overscroll-behavior: contain; scrollbar-gutter: stable; border-radius: 22px; border: 1px solid rgba(167,139,250,.38); background: linear-gradient(145deg, rgba(22,25,38,.99), rgba(7,9,15,.995)); box-shadow: 0 28px 90px rgba(0,0,0,.72), 0 0 48px rgba(124,58,237,.22); }
  .sg-control-dialog { width: 90vw; height: 90vh; max-width: 90vw; max-height: 90vh; overflow: hidden; box-sizing: border-box; display: grid; grid-template-rows: auto minmax(0, 1fr) auto; border-radius: 18px; border: 1px solid rgba(167,139,250,.38); background: linear-gradient(145deg, rgba(28,25,72,.99), rgba(9,11,25,.99)); box-shadow: 0 28px 90px rgba(0,0,0,.72), 0 0 42px rgba(124,58,237,.28); }
  .sg-control-head { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 22px 16px; background: rgba(12,13,31,.96); border-bottom: 1px solid rgba(255,255,255,.10); backdrop-filter: blur(14px); }
  .sg-control-head h2 { margin: 0; font-size: 23px; }
  .sg-control-body { min-height: 0; padding: 22px; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; scrollbar-gutter: stable; }
  .sg-control-footer { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; padding: 14px 22px; background: rgba(12,13,31,.97); border-top: 1px solid rgba(255,255,255,.10); box-shadow: 0 -12px 28px rgba(5,6,18,.34); }
  .sg-control-section + .sg-control-section { margin-top: 22px; padding-top: 20px; border-top: 1px solid rgba(255,255,255,.09); }
  .sg-control-section h3 { margin: 0 0 10px; color: #c4b5fd; font-size: 11px; letter-spacing: .13em; text-transform: uppercase; }
  .sg-control-results { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); gap: 9px; margin-top: 14px; }
  .sg-control-result { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 11px 13px; border-radius: 11px; border: 1px solid rgba(255,255,255,.10); background: rgba(255,255,255,.045); color: #fff; cursor: pointer; text-align: left; }
  .sg-control-result:hover { border-color: rgba(167,139,250,.55); background: rgba(167,139,250,.10); }
  .sg-control-result small { color: rgba(226,232,240,.60); display: block; margin-top: 2px; }
  .sg-filter-workspace-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; align-items: start; }
  .sg-filter-workspace-grid > .sg-control-section { min-height: 150px; margin: 0; padding: 18px; border: 1px solid rgba(255,255,255,.09); border-radius: 14px; background: rgba(255,255,255,.035); }
  .sg-filter-match-count { margin-top: 20px; padding: 14px 16px; border-radius: 12px; background: rgba(124,58,237,.10); border: 1px solid rgba(167,139,250,.20); }
  @media (max-width: 1050px) { .sg-filter-workspace-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 900px) {
    .sg-control-backdrop { padding: 12px; }
    .sg-control-dialog { width: calc(100vw - 24px); height: calc(100vh - 24px); max-width: calc(100vw - 24px); max-height: calc(100vh - 24px); }
  }
  @media (max-width: 700px) { .sg-filter-workspace-grid { grid-template-columns: 1fr; } .sg-control-body { padding: 17px; } .sg-control-footer { padding: 12px 17px; } }
  @media (max-width: 480px) {
    .sg-control-backdrop { padding: 6px; }
    .sg-control-dialog { width: calc(100vw - 12px); height: calc(100vh - 12px); max-width: calc(100vw - 12px); max-height: calc(100vh - 12px); border-radius: 14px; }
    .sg-control-head { padding: 15px 16px 13px; }
    .sg-control-head h2 { font-size: 19px; }
    .sg-control-body { padding: 14px; }
    .sg-control-footer { padding: 10px 14px; }
  }
  .sg-info-hero-wrap { position: relative; min-height: 390px; overflow: hidden; border-radius: 21px 21px 0 0; background: #090b14; }
  .sg-info-hero { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
  .sg-info-hero-shade { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(5,7,14,.98) 0%, rgba(5,7,14,.82) 43%, rgba(5,7,14,.08) 82%), linear-gradient(0deg, rgba(5,7,14,.92), transparent 56%); }
  .sg-info-hero-copy { position: relative; z-index: 1; width: min(650px, 70%); min-height: 390px; box-sizing: border-box; padding: 52px 42px 40px; display: flex; flex-direction: column; justify-content: flex-end; }
  .sg-info-hero-copy h2 { margin: 8px 0 14px; color: #fff; font-size: clamp(34px, 5vw, 64px); line-height: .96; letter-spacing: -.045em; }
  .sg-info-hero-copy p { margin: 0; color: rgba(240,244,255,.80); font-size: 17px; line-height: 1.65; }
  .sg-info-close { position: sticky; top: 14px; z-index: 6; float: right; margin: 14px 14px -58px 0; backdrop-filter: blur(12px); }
  .sg-info-body { clear: both; padding: 34px 42px 46px; }
  .sg-info-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin: 0 0 34px; overflow: hidden; border: 1px solid rgba(255,255,255,.1); border-radius: 13px; background: rgba(255,255,255,.1); }
  .sg-info-fact { min-height: 70px; padding: 15px 17px; background: #10131e; }
  .sg-info-fact strong { display: block; color: #fff; font-size: 14px; }
  .sg-info-fact span { display: block; margin-top: 5px; color: rgba(226,232,240,.56); font-size: 10px; font-weight: 850; letter-spacing: .1em; text-transform: uppercase; }
  .sg-info-section { margin-top: 38px; }
  .sg-info-section-head { display: grid; grid-template-columns: .72fr 1.28fr; gap: 35px; align-items: end; margin-bottom: 20px; }
  .sg-info-section-head h3 { margin: 7px 0 0; color: #fff; font-size: clamp(25px, 3.4vw, 40px); line-height: 1.04; letter-spacing: -.03em; }
  .sg-info-section-head p { margin: 0; color: rgba(226,232,240,.68); font-size: 15px; line-height: 1.65; }
  .sg-info-eyebrow { color:#8b2be2; font-size:clamp(22px,2vw,28px); font-weight:900; line-height:1.15; letter-spacing:.035em; text-transform:uppercase; }
  .sg-info-capabilities { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 11px; }
  .sg-info-capability { min-height: 92px; padding: 18px; border: 1px solid rgba(167,139,250,.22); border-radius: 12px; background: linear-gradient(145deg, rgba(124,58,237,.11), rgba(255,255,255,.025)); color: rgba(244,247,255,.88); font-size: 14px; font-weight: 760; line-height: 1.45; }
  .sg-info-capability b { display: block; margin-bottom: 10px; color: #a78bfa; font-size: 11px; }
  .sg-info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-top: 18px; }
  .sg-info-panel { padding: 22px; border-radius: 14px; background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.09); }
  .sg-info-panel h3 { margin: 0 0 9px; color: #c4b5fd; font-size: 11px; letter-spacing: .12em; text-transform: uppercase; }
  .sg-info-panel p, .sg-info-panel li { color: rgba(237,241,248,.76); font-size: 14px; line-height: 1.65; }
  .sg-info-panel p { margin: 0; }
  .sg-info-panel ul { margin: 0; padding-left: 18px; }
  .sg-info-real-ui { margin: 42px 0 0; }
  .sg-info-real-ui img { display: block; width: 100%; height: auto; border-radius: 13px; border: 1px solid rgba(167,139,250,.24); box-shadow: 0 22px 58px rgba(0,0,0,.42); }
  .sg-info-real-ui figcaption { margin-top: 10px; color: rgba(226,232,240,.54); font-size: 12px; line-height: 1.5; }
  @media (max-width: 760px) { .sg-info-backdrop { padding: 6px; } .sg-info-dialog { max-height: calc(100vh - 12px); border-radius: 15px; } .sg-info-hero-wrap, .sg-info-hero-copy { min-height: 470px; } .sg-info-hero-copy { width: 100%; padding: 110px 20px 28px; } .sg-info-hero-shade { background: linear-gradient(0deg, rgba(5,7,14,.99) 8%, rgba(5,7,14,.82) 65%, rgba(5,7,14,.22)); } .sg-info-body { padding: 25px 18px 34px; } .sg-info-facts, .sg-info-grid, .sg-info-section-head { grid-template-columns: 1fr; } .sg-info-section-head { gap: 12px; } }

  /* Full-screen CyberTech-derived More Info experience. */
  .sg-info-backdrop { display: block; padding: 0; background: #061018; }
  .sg-info-dialog { width: 100vw; max-width:none; height:100vh; max-height:none; border:0; border-radius:0; background:#fff; color:#183039; box-shadow:none; }
  .sg-info-close { position: fixed; top: 22px; right: 22px; float: none; margin: 0; z-index: 1002; }
  .sg-info-hero-wrap { min-height: 760px; display:grid; grid-template-columns:minmax(430px,.8fr) minmax(0,1.2fr); border-radius: 0; background:#071018; }
  .sg-info-hero { position:static; grid-column:2; grid-row:1; width:100%; height:100%; min-height:760px; object-fit:cover; filter:none; }
  .sg-info-hero-shade { display:none; }
  .sg-info-hero-copy { position:static; grid-column:1; grid-row:1; width:auto; min-height:760px; margin:0; padding:96px clamp(36px,5vw,72px) 64px; justify-content:flex-end; background:#071018; }
  .sg-info-hero-copy h2 { max-width: 720px; font-size: clamp(3.8rem,6.8vw,7.2rem); line-height: .9; letter-spacing: -.07em; text-wrap:balance; }
  .sg-info-hero-copy p { max-width: 720px; font-size: 18px; }
  .sg-info-nav { position: sticky; top: 0; z-index: 1001; display: flex; gap: 28px; overflow-x: auto; padding: 0 max(28px,calc((100vw - 1180px)/2)); border-bottom: 1px solid #d8e5e7; background: rgba(255,255,255,.97); backdrop-filter: blur(12px); }
  .sg-info-nav a { flex: 0 0 auto; padding: 17px 0 15px; color: #40555e; font-size: 11px; font-weight: 850; text-decoration: none; text-transform: uppercase; }
  .sg-info-body { width:min(100% - 56px,1180px); margin:0 auto; padding:0 0 90px; }
  .sg-info-facts { width:100%; margin:0 0 38px; border:0; border-radius:0; background:#091820; }
  .sg-info-fact { min-height:116px; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:20px; background:#091820; text-align:center; }
  .sg-info-fact strong { font-size:clamp(17px,1.5vw,22px); line-height:1.2; }
  .sg-info-fact span { margin-top:9px; font-size:12px; line-height:1.25; }
  .sg-info-section { margin-top:0; padding:38px 0 52px; scroll-margin-top:52px; }
  .sg-info-section-head { grid-template-columns: .9fr 1.1fr; gap: 70px; margin-bottom: 48px; }
  .sg-info-section-head h3 { color: #132b34; font-size: clamp(1.1rem,2.25vw,2.4rem); line-height: .98; letter-spacing: -.055em; }
  .sg-info-section-head p { color: #60737b; font-size: 17px; }
  .sg-info-media-split { display: grid; grid-template-columns: 1.08fr .92fr; gap: 60px; align-items: center; }
  .sg-info-picture { display:grid; grid-template-rows:minmax(540px,1fr) auto; margin: 0; overflow: hidden; border-radius: 4px; background: #071018; box-shadow: 0 25px 60px rgba(13,28,35,.2); }
  .sg-info-picture img { position:static; width:100%; height:100%; min-height:540px; object-fit:cover; filter:none; }
  .sg-info-picture::after { display:none; }
  .sg-info-picture figcaption { position:static; padding:18px 22px; color:#f2f7f8; background:#071018; font-size:16px; line-height:1.5; }
  .sg-info-capabilities { grid-template-columns: 1fr; gap: 0; }
  .sg-info-capability { min-height: 0; padding: 20px 0; border: 0; border-top: 1px solid #c4d3d5; border-radius: 0; background: transparent; color: #314a54; font-size: 16px; }
  .sg-info-capability { display:grid; grid-template-columns:58px minmax(0,1fr); align-items:start; column-gap:8px; }
  .sg-info-capability b { display:block; width:auto; color:#7c3aed; font-size:22px; line-height:1.05; }
  .sg-info-capability span { display:block; min-width:0; }
  .sg-info-cinema { min-height:620px; display:grid; grid-template-columns:minmax(0,1.25fr) minmax(430px,.75fr); overflow:hidden; margin:40px calc((1180px - 100vw)/2) 48px; scroll-margin-top:52px; background:#071018; }
  .sg-info-cinema > img { position:static; grid-column:1; grid-row:1; width:100%; height:100%; min-height:620px; object-fit:cover; filter:none; }
  .sg-info-cinema-shade { display:none; }
  .sg-info-cinema-copy { position:static; grid-column:2; grid-row:1; width:auto; margin:0; padding:60px 48px; align-self:center; background:#071018; }
  .sg-info-cinema-copy h3 { max-width:820px; margin:12px 0 18px; color:#fff; font-size:clamp(1.175rem,1.85vw,2rem); line-height:.98; letter-spacing:-.05em; text-wrap:balance; }
  .sg-info-cinema-copy p { max-width:700px; color:#c3d2d8; font-size:18px; line-height:1.7; }
  .sg-info-section-head .sg-info-eyebrow { color:#6d21c9; }
  .sg-info-cinema-copy .sg-info-eyebrow,.sg-info-output-visual .sg-info-eyebrow,.sg-info-access .sg-info-eyebrow { color:#d39cff; }
  .sg-info-grid { grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; margin:0 0 48px; padding:0; background:#fff; scroll-margin-top:52px; }
  .sg-info-panel { min-height:180px; padding:32px; border:1px solid #d8e5e7; border-radius:0; background:#fff; }
  .sg-info-panel h3 { margin:0 0 14px; color:#17323c; font-size:clamp(1.25rem,1.8vw,1.65rem); line-height:1.15; letter-spacing:-.02em; text-transform:none; }
  .sg-info-panel p,.sg-info-panel li { color:#4e636c; font-size:16px; }
  .sg-info-real-ui { margin:0 0 48px; }
  .sg-info-real-ui img { border-radius:4px; }
  .sg-info-output-visual { min-height:620px; display:grid; grid-template-columns:minmax(0,1.2fr) minmax(440px,.8fr); overflow:hidden; margin:0 0 48px; background:#071018; }
  .sg-info-output-visual > img { display:block; grid-column:1; grid-row:1; width:100%; height:100%; min-height:620px; object-fit:cover; filter:none; }
  .sg-info-output-visual > div { grid-column:2; grid-row:1; align-self:center; padding:58px 50px; color:#dce9ed; background:#071018; }
  .sg-info-output-visual h3 { margin:12px 0 20px; color:#fff; font-size:clamp(1.15rem,1.9vw,2rem); line-height:1; text-wrap:balance; }
  .sg-info-output-visual ul { columns:auto; padding-left:18px; font-size:16px; line-height:1.65; }
  .sg-info-output-visual li { margin-bottom:8px; break-inside:avoid; text-wrap:pretty; }
  .sg-info-access { display:grid; grid-template-columns:minmax(0,3fr) minmax(300px,1fr); gap:0; align-items:stretch; overflow:hidden; padding:0; background:#091820; color:#dce9ed; scroll-margin-top:52px; }
  .sg-info-access > img { display:block; width:100%; height:auto; min-height:0; align-self:center; object-fit:contain; background:#091820; filter:saturate(1.1) contrast(1.04); }
  .sg-info-access-copy { display:flex; flex-direction:column; justify-content:flex-start; padding:42px 34px; }
  .sg-info-access-copy > .sg-info-eyebrow { margin:0 0 24px; }
  .sg-info-access h3 { margin:0 0 24px; color:#fff; font-size:clamp(1.6rem,2.1vw,2.25rem); line-height:1.08; text-wrap:balance; }
  .sg-info-access p { margin:0; color:#b8c8ce; font-size:16px; line-height:1.7; }
  .sg-info-access-link { display:inline-flex; align-self:flex-start; justify-content:center; margin-top:auto; padding:14px 22px; border:1px solid #b76cff; border-radius:999px; color:#fff; background:#6d21c9; font-size:15px; font-weight:850; text-decoration:none; box-shadow:0 0 24px rgba(139,43,226,.28); }
  @media (max-width:1180px) { .sg-info-cinema { margin-left:-28px; margin-right:-28px; } }
  @media (max-width:900px) { .sg-info-media-split,.sg-info-section-head { grid-template-columns:1fr; gap:28px; }.sg-info-picture { min-height:560px; }.sg-info-hero-wrap,.sg-info-cinema,.sg-info-output-visual{grid-template-columns:1fr;}.sg-info-hero-copy{grid-column:1;grid-row:1;min-height:0;padding:88px 28px 42px}.sg-info-hero{grid-column:1;grid-row:2;min-height:520px}.sg-info-cinema>img{grid-column:1;grid-row:1;min-height:520px}.sg-info-cinema-copy{grid-column:1;grid-row:2;padding:48px 30px}.sg-info-output-visual>img{grid-column:1;grid-row:1;min-height:520px}.sg-info-output-visual>div{grid-column:1;grid-row:2}.sg-info-access{grid-template-columns:1fr}.sg-info-access>img{min-height:0}.sg-info-access-copy{padding:38px 26px} }
  @media (max-width:760px) { .sg-info-backdrop{padding:0}.sg-info-dialog{height:100vh;max-height:none;border-radius:0}.sg-info-hero{min-height:420px}.sg-info-hero-copy{min-height:0;padding:90px 20px 38px}.sg-info-hero-copy h2{font-size:clamp(3.2rem,17vw,6rem)}.sg-info-body{width:min(100% - 30px,1180px);padding:0 0 80px}.sg-info-facts,.sg-info-grid{grid-template-columns:1fr}.sg-info-cinema{min-height:0;margin-left:-15px;margin-right:-15px}.sg-info-cinema>img{min-height:420px}.sg-info-cinema-copy{padding:42px 22px}.sg-info-picture{min-height:430px;grid-template-rows:minmax(400px,1fr) auto}.sg-info-picture img{min-height:400px}.sg-info-output-visual{min-height:0}.sg-info-output-visual>img{min-height:420px}.sg-info-output-visual>div{padding:38px 22px}.sg-info-access{padding:0}.sg-info-close{top:12px;right:12px}.sg-info-section-head{gap:14px} }
  .sg-pricing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: 10px; }
  .sg-purchase-grid { display: grid; grid-template-columns: repeat(4, minmax(150px, 1fr)); gap: 12px; align-items: stretch; }
  @media (max-width: 1000px) { .sg-purchase-grid { grid-template-columns: repeat(2, minmax(150px, 1fr)); } }
  @media (max-width: 600px) { .sg-pricing-grid, .sg-purchase-grid { grid-template-columns: 1fr; } }
`;

function GeneratorControlDialog({ id, title, onClose, returnFocusRef, initialFocusRef, footer, children }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const returnTarget = returnFocusRef?.current || document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = setTimeout(() => {
      const target = initialFocusRef?.current
        || dialogRef.current?.querySelector('button, input, select, [tabindex]:not([tabindex="-1"])');
      target?.focus();
    }, 0);
    function onKeyDown(event) {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      returnTarget?.focus?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="sg-control-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} className="sg-control-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
        <div className="sg-control-head">
          <h2 id={`${id}-title`}>{title}</h2>
          <button type="button" className="sg-pill" style={sg.pillSm} onClick={onClose} aria-label={`Close ${title}`}>Close</button>
        </div>
        <div className="sg-control-body">{children}</div>
        {footer && <div className="sg-control-footer">{footer}</div>}
      </section>
    </div>
  );
}

const GENERATOR_AUDIENCES = {
  'DM & SESSION': 'Dungeon Masters who need useful, bounded material during preparation or live play.',
  'CAMPAIGN & STORY': 'Dungeon Masters, campaign builders and writers shaping connected adventures and longer arcs.',
  'NPC & SOCIAL': 'Dungeon Masters and writers preparing people, relationships and social consequences.',
  'WORLD & SETTLEMENT': 'Worldbuilders and Dungeon Masters developing places, cultures, routes and persistent change.',
  'PLAYER & PROGRESSION': 'Players and Dungeon Masters developing characters, party history and ongoing progression.',
  'ITEMS & COMMERCE': 'Dungeon Masters building usable objects, rewards, trade and ownership context.',
  'RULES & RESOLUTION': 'Dungeon Masters who want table-ready support while keeping final rules adjudication at the table.',
  'ENCOUNTERS & CHALLENGES': 'Dungeon Masters preparing or running playable pressure, opposition and tactical situations.',
};

const GENERATOR_MORE_INFO_VISUALS = {
  'pet-animal-companion-generator': {
    capability: {
      src: '/product-more-info/pet-animal-companion-generator/capability-real-fantasy-custom-companions.png',
      alt: 'A ranger with a wolfhound, a scholar with a feathered drake and a halfling caretaker with a custom fox-like companion in a bright mountain sanctuary.',
      caption: 'Real, fantasy and custom companions each become distinct individuals with their own temperament, bond, stage and role potential.',
    },
    workflow: {
      src: '/product-more-info/pet-animal-companion-generator/workflow-acquisition-growth-bond-and-roles.png',
      alt: 'Two campaign planners follow one rescued wolfhound from pup through growth and role development using a physical lifecycle model and campaign journal.',
    },
    output: {
      src: '/product-more-info/pet-animal-companion-generator/output-persistent-companion-record.png',
      alt: 'A mature wolfhound returns through a village gate beside its adventurer, with its role harness, travel journal and portrait record nearby.',
    },
  },
  'animal-equipment-care-habitat-generator': {
    capability: {
      src: '/product-more-info/animal-equipment-care-habitat-generator/capability-equipment-care-and-habitat.png',
      alt: 'A young griffin is fitted with wing-safe equipment beside organised grooming tools, feed, water, bedding, travel packs and a secure mountain habitat.',
      caption: 'Fit, care, supplies, housing and travel preparation stay connected to the same animal as it grows and its needs change.',
    },
    workflow: {
      src: '/product-more-info/animal-equipment-care-habitat-generator/workflow-fit-growth-care-and-travel.png',
      alt: 'Caretakers compare juvenile, adolescent and adult griffin models with changing harness fit, seasonal care, habitat and mountain travel preparations.',
    },
    output: {
      src: '/product-more-info/animal-equipment-care-habitat-generator/output-complete-companion-support-plan.png',
      alt: 'A mature griffin stands ready at a heated cliffside roost with correctly fitted tack, care supplies, repair tools and balanced expedition packs.',
    },
  },
  'animal-training-trainer-generator': {
    capability: {
      src: '/product-more-info/animal-training-trainer-generator/capability-training-programmes-and-trainers.png',
      alt: 'A cavalry master, patient stable trainer and arcane beast handler prepare distinct training programmes for horses, hounds and fantastical companions in a bright academy yard.',
      caption: 'Training programmes, prerequisite chains and trainer tiers turn a desired role into a practical campaign commitment instead of an instant unlock.',
    },
    workflow: {
      src: '/product-more-info/animal-training-trainer-generator/workflow-prerequisites-schedule-and-progress.png',
      alt: 'A trainer and rider review prerequisite cards, a campaign calendar and measured progress while a young horse advances through staged lessons.',
    },
    output: {
      src: '/product-more-info/animal-training-trainer-generator/output-trained-capability-and-role-readiness.png',
      alt: 'A fully trained warhorse performs a controlled battlefield manoeuvre beside its rider while completed programme records and role readiness notes sit nearby.',
    },
  },
};

function catalogueInformation(item) {
  const capabilities = item.capabilities || [];
  const focus = item.name.replace(/\s+(Generator|Narrator|Engine|System)$/i, '');
  return {
    summary: item.description,
    different: `${item.name} is a focused ${item.category.toLowerCase()} product for ${focus.toLowerCase()}. Its published capability set is kept specific to that job rather than presented as a general-purpose text prompt.`,
    quick_workflow: capabilities.length
      ? `Start with the subject and context needed for “${capabilities[0]}”. Generate and review the result across “${capabilities.slice(1, 3).join('” and “')}”, then use the remaining product capabilities to refine or carry the result forward.`
      : `Set the subject and context presented by ${item.name}, generate, review the structured result and keep only material that fits the table.`,
    result_sections: capabilities,
    platform: 'Runs inside the signed-in VISINVOKE Desktop Control Centre. The catalogue card controls access; opening More Info never purchases or launches the generator.',
    authorityNote: 'This explanation uses the generator’s registered catalogue purpose and shipped capability authority.',
  };
}

function ProductInformationDialog({ item, priceLabel, onClose }) {
  const authored = item.product_information || null;
  const info = authored || catalogueInformation(item);
  const capabilities = item.capabilities || [];
  const visuals = GENERATOR_MORE_INFO_VISUALS[item.slug] || null;
  const dialogRef = useRef(null);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previousFocus?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="sg-info-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} tabIndex={-1} className="sg-info-dialog" role="dialog" aria-modal="true" aria-labelledby="sg-info-title">
        <button type="button" className="sg-pill sg-info-close" style={sg.pillSm} onClick={onClose} aria-label="Close product information">Close</button>
        <header className="sg-info-hero-wrap">
          {item.artwork_path && <img className="sg-info-hero" src={item.artwork_path} alt={`${item.name} product artwork`} />}
          <div className="sg-info-hero-shade" />
          <div className="sg-info-hero-copy">
            <div className="sg-info-eyebrow">{item.category}</div>
            <h2 id="sg-info-title">{item.name}</h2>
            <p>{info.summary || item.description}</p>
          </div>
        </header>
        <div className="sg-info-facts" aria-label="Product access facts">
          <div className="sg-info-fact"><strong>{priceLabel || '$1.99 / month'}</strong><span>Individual access</span></div>
          <div className="sg-info-fact"><strong>{item.available === false ? 'Coming soon' : 'Available now'}</strong><span>Catalogue status</span></div>
          <div className="sg-info-fact"><strong>Desktop Control Centre</strong><span>Product home</span></div>
          <div className="sg-info-fact"><strong>{info.fact_value || (authored ? 'Detailed guide' : 'Generator guide')}</strong><span>{info.fact_label || 'More information'}</span></div>
        </div>
        <div className="sg-info-body">
          <section className="sg-info-section" id="sg-capabilities">
            <div className="sg-info-section-head">
              <div><div className="sg-info-eyebrow">What you can create</div><h3>{info.capability_heading || `Create with ${item.name}`}</h3></div>
              <p>{info.capability_copy || 'Choose how much detail you want, shape the result and keep the parts that fit your game.'}</p>
            </div>
            <div className="sg-info-media-split">
              {(visuals?.capability?.src || item.artwork_path) ? <figure className="sg-info-picture"><img src={visuals?.capability?.src || item.artwork_path} alt={visuals?.capability?.alt || `${item.name} visual capability evidence`} /><figcaption>{visuals?.capability?.caption || 'The generator’s own catalogue artwork anchors this product-specific explanation.'}</figcaption></figure> : null}
              <div className="sg-info-capabilities">
                {capabilities.map((point, index) => <div className="sg-info-capability" key={point}><b>{String(index + 1).padStart(2, '0')}</b><span>{point}</span></div>)}
              </div>
            </div>
          </section>
          <section className="sg-info-cinema" id="sg-workflow">
            {(visuals?.workflow?.src || item.artwork_path) ? <img src={visuals?.workflow?.src || item.artwork_path} alt={visuals?.workflow?.alt || ''} aria-hidden={visuals?.workflow?.alt ? undefined : 'true'} /> : null}
            <div className="sg-info-cinema-shade" />
            <div className="sg-info-cinema-copy"><div className="sg-info-eyebrow">How the generator works</div><h3>{info.workflow_heading || `Create, review and refine your ${item.name.replace(/\s+Generator$/i, '').toLowerCase()}`}</h3><p>{info.workflow_copy || 'Choose the options that fit your game, generate a result, then keep, edit or reroll the parts you want to change.'}</p></div>
          </section>
          <div className="sg-info-grid" id="sg-output">
            <div className="sg-info-panel"><h3>Why it is different</h3><p>{info.different || item.description}</p></div>
            <div className="sg-info-panel"><h3>Quick workflow</h3><p>{info.quick_workflow}</p></div>
            {info.deep_workflow && <div className="sg-info-panel"><h3>Deep workflow</h3><p>{info.deep_workflow}</p></div>}
            <div className="sg-info-panel"><h3>What it produces</h3>
              <ul>{(info.result_sections || capabilities).map((section) => <li key={section}>{section}</li>)}</ul>
            </div>
            <div className="sg-info-panel"><h3>Who it is for</h3><p>{info.audience || GENERATOR_AUDIENCES[item.category] || 'Dungeon Masters, players and creators using this generator.'}</p></div>
            {info.persistence && <div className="sg-info-panel"><h3>History and persistence</h3><p>{info.persistence}</p></div>}
            {info.exports && <div className="sg-info-panel"><h3>Copy and export</h3><p>{info.exports}</p></div>}
            {info.handoffs && <div className="sg-info-panel"><h3>Handoffs</h3><p>{info.handoffs}</p></div>}
            {info.platform && <div className="sg-info-panel"><h3>Platform and access</h3><p>{info.platform}</p></div>}
            <div className="sg-info-panel"><h3>{info.scope_heading || 'Made for its purpose'}</h3><p>{info.scope_copy || `${item.name} concentrates on the result it was designed to create, so the finished material stays clear and useful.`}</p></div>
          </div>
          {visuals?.evidence?.src && (
            <figure className="sg-info-real-ui">
              <img src={visuals.evidence.src} alt={visuals.evidence.alt} />
              <figcaption>{visuals.evidence.caption}</figcaption>
            </figure>
          )}
          <section className="sg-info-output-visual">
            {(visuals?.output?.src || item.artwork_path) ? <img src={visuals?.output?.src || item.artwork_path} alt={visuals?.output?.alt || ''} aria-hidden={visuals?.output?.alt ? undefined : 'true'} /> : null}
            <div><div className="sg-info-eyebrow">What the result contains</div><h3>{info.result_heading || `A complete ${item.name.replace(/\s+Generator$/i, '').toLowerCase()} result`}</h3><ul>{capabilities.map((section) => <li key={section}>{section}</li>)}</ul></div>
          </section>
          <section className="sg-info-access" id="sg-access"><img src="/product-more-info/shared/desktop-control-centre-cropped.png" alt="VISINVOKE Desktop Control Centre showing products and tools in the command centre" /><div className="sg-info-access-copy"><div className="sg-info-eyebrow">Continue in VISINVOKE</div><h3>{info.access_heading || 'VISINVOKE products, ready on your desktop'}</h3><p>{info.access_copy || `Visit VISINVOKE to sign in, create your free account and browse everything VISINVOKE offers. Then download and install the Desktop Control Centre to open ${item.name} and your premium products directly from your desktop whenever you need them.`}</p><a className="sg-info-access-link" href="https://visinvoke.com/" target="_blank" rel="noreferrer">Visit VISINVOKE.com</a></div></section>
          {info.authorityNote && <p style={{ margin: '26px 0 0', color: 'rgba(226,232,240,.45)', fontSize: 11.5 }}>{info.authorityNote}</p>}
        </div>
      </section>
    </div>
  );
}

export default function StandaloneGeneratorsPage() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const location = useLocation();
  const moreInfoSlug = location.pathname.match(/\/standalone-generators\/([^/]+)\/more-info\/?$/)?.[1] ||
    location.pathname.match(/\/preview\/generators\/([^/]+)\/more-info\/?$/)?.[1] || null;
  const [catalogue, setCatalogue] = useState(null);
  const [account, setAccount] = useState(null);
  const [selected, setSelected] = useState([]);
  const [quote, setQuote] = useState(null);
  const [category, setCategory] = useState(() => readSavedView().category || 'All');
  const [query, setQuery] = useState(() => readSavedView().query || '');
  const [sort, setSort] = useState(() => readSavedView().sort || 'featured');
  const [ownedOnly, setOwnedOnly] = useState(() => !!readSavedView().ownedOnly);
  const [availOnly, setAvailOnly] = useState(() => !!readSavedView().availOnly);
  const [page, setPage] = useState(() => Number(readSavedView().page) || 1);
  // The generator experience comes from the URL (?group=), set by the Gaming page group cards. It is
  // navigation, not a saved filter: arriving at the plain route always shows the complete collection.
  const [group, setGroup] = useState(ALL_GROUPS);
  const groupResolved = useRef(false);
  const commerceIntentResolved = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [changeOpen, setChangeOpen] = useState(false);
  const [infoItem, setInfoItem] = useState(null);
  const [controlModal, setControlModal] = useState(null);
  const [pageSize, setPageSize] = useState(() => generatorPageSizeForWidth(
    typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth,
  ));
  const findButtonRef = useRef(null);
  const filtersButtonRef = useRef(null);
  const pricingButtonRef = useRef(null);
  const searchInputRef = useRef(null);

  async function refresh() {
    const [catalogueRes, accountRes] = await Promise.all([
      API.get('/v1/combat-commerce/catalogue'),
      API.get('/v1/combat-commerce/account'),
    ]);
    const liveCatalogue = catalogueRes.data.standalone_generators;
    const liveItems = (liveCatalogue.items || []).map(withCanonicalArtwork);
    setCatalogue({
      ...liveCatalogue,
      items: liveItems,
    });
    if (!groupResolved.current) {
      groupResolved.current = true;
      setGroup(groupFromSearch(typeof window === 'undefined' ? '' : window.location.search, serverGroups(liveCatalogue)));
    }
    setAccount(accountRes.data);
    const sub = accountRes.data.generator_subscription;
    const currentSelection = sub ? sub.selected_slugs || [] : [];
    setSelected(currentSelection);
    if (!commerceIntentResolved.current) {
      commerceIntentResolved.current = true;
      const intent = generatorCommerceIntent(typeof window === 'undefined' ? '' : window.location.search);
      const item = intent && liveItems.find((row) => row.slug === intent.slug);
      if (item) {
        setGroup(ALL_GROUPS); setCategory('All'); setQuery(item.name); setPage(1);
        if (intent.action === 'info') setInfoItem(item);
        if (intent.action === 'buy' && item.available !== false && !(accountRes.data.generator_entitlements || []).includes(item.slug)) {
          if (sub) setChangeOpen(true);
          else setSelected(Array.from(new Set(currentSelection.concat([item.slug]))));
        }
      }
    }
  }

  useEffect(() => { refresh().catch((e) => setError(e.response?.data?.error || 'Unable to load generators.')); }, []);

  useEffect(() => {
    if (!catalogue || !moreInfoSlug) return;
    const routedItem = catalogue.items.find((item) => item.slug === moreInfoSlug);
    setInfoItem(routedItem || null);
    if (!routedItem) setError('That generator is not available in the catalogue.');
  }, [catalogue, moreInfoSlug]);

  // Keep the JS page capacity aligned with the CSS grid. Preserve the first item represented by
  // the current page when a responsive breakpoint changes rather than dropping a real card slot.
  useEffect(() => {
    function updatePageSize() {
      const nextSize = generatorPageSizeForWidth(window.innerWidth);
      setPageSize((currentSize) => {
        if (nextSize === currentSize) return currentSize;
        setPage((currentPage) => Math.floor(((currentPage - 1) * currentSize) / nextSize) + 1);
        return nextSize;
      });
    }
    window.addEventListener('resize', updatePageSize);
    return () => window.removeEventListener('resize', updatePageSize);
  }, []);

  // The recommendation is the SERVER's, not this page's: it prices the selection through the same
  // authority that validates the purchase, so what is shown is what will be charged.
  useEffect(() => {
    let alive = true;
    if (account?.generator_subscription) { setQuote(null); return undefined; }
    if (!selected.length) { setQuote(null); return undefined; }
    API.post('/v1/combat-commerce/generators/quote', { selected_slugs: selected })
      .then((res) => { if (alive) setQuote(res.data); })
      .catch(() => { if (alive) setQuote(null); });
    return () => { alive = false; };
  }, [selected, account]);

  useEffect(() => {
    let style = document.getElementById('standalone-generators-css');
    if (!style) { style = document.createElement('style'); style.id = 'standalone-generators-css'; document.head.appendChild(style); }
    style.textContent = SG_CSS;
  }, []);

  // Reset to the first page whenever the result set changes - but not on initial mount, so a
  // session-restored page remains stable.
  const didInitPage = useRef(false);
  useEffect(() => {
    if (!didInitPage.current) { didInitPage.current = true; return; }
    setPage(1);
  }, [query, category, sort, ownedOnly, availOnly, group]);

  // Persist the catalogue view (search, category, sort, filters and page).
  useEffect(() => {
    try {
      sessionStorage.setItem(SG_STATE_KEY, JSON.stringify({ query, category, sort, ownedOnly, availOnly, page }));
    } catch { /* sessionStorage unavailable - non-fatal */ }
  }, [query, category, sort, ownedOnly, availOnly, page]);

  // A restored category that the live catalogue no longer carries falls back to All rather than
  // silently showing an empty grid.
  useEffect(() => {
    if (!catalogue) return;
    const present = new Set(catalogue.items.map((i) => i.category));
    if (category !== 'All' && !present.has(category)) setCategory('All');
  }, [catalogue]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!catalogue || !account) {
    return <CombatProShell title="Standalone Generators" subtitle="Loading your catalogue…" navigate={navigate}><div style={cps.card}>{error || 'Loading…'}</div></CombatProShell>;
  }

  // Pills follow the approved taxonomy order, but only for categories the live catalogue actually
  // has — an empty pill would advertise a group with nothing in it.
  const present = new Set(catalogue.items.map((item) => item.category));
  const categories = ['All', ...CATEGORY_ORDER.filter((name) => present.has(name)),
    ...[...present].filter((name) => !CATEGORY_ORDER.includes(name)).sort()];

  // Featured products get the deeper treatment above the grid and are NEVER repeated inside it.
  const featured = FEATURED_SLUGS
    .map((slug) => catalogue.items.find((item) => item.slug === slug))
    .filter(Boolean);
  const featuredSlugs = new Set(featured.map((item) => item.slug));

  const matches = (item) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    // `keywords` is discovery metadata from the catalogue. It is searched, never rendered.
    const haystack = [item.name, item.description, item.category, item.keywords || '',
      ...(item.capabilities || [])].join(' ').toLowerCase();
    return haystack.includes(needle);
  };
  const inCategory = (item) => category === 'All' || item.category === category;
  const experienceGroups = serverGroups(catalogue);
  const activeGroup = experienceGroups.find((row) => row.key === group) || null;
  const inGroup = (item) => itemInGroup(item, activeGroup ? activeGroup.key : ALL_GROUPS);
  const groupItems = orderForGroup(catalogue.items, activeGroup ? activeGroup.key : ALL_GROUPS);
  function selectGroup(key) {
    setGroup(key);
    navigate(groupRoute(GENERATORS_ROUTE, key), { replace: true });
  }
  const entitled = new Set(account.generator_entitlements || []);
  // Owned / Available toggles are real-data filters: entitlement comes from the account, availability
  // from the catalogue item. (Category, name, description, keywords and capabilities are searched via
  // matches(); the catalogue carries no family, section or release-date field, so no family/newest
  // filter is invented here.)
  const passesToggles = (item) =>
    (!ownedOnly || entitled.has(item.slug)) &&
    (!availOnly || item.available !== false);
  const visibleFeatured = featured.filter((item) => inGroup(item) && inCategory(item) && matches(item) && passesToggles(item));

  const { total: totalVisible, pageCount, currentPage, pageItems } = selectGeneratorGrid({
    items: groupItems,
    excludeSlugs: featuredSlugs,
    predicate: (item) => inGroup(item) && inCategory(item) && matches(item) && passesToggles(item),
    sort,
    page,
    perPage: pageSize,
  });
  const hasActiveFilters = query.trim() !== '' || category !== 'All' || ownedOnly || availOnly || sort !== 'featured';
  function clearAll() { setQuery(''); setCategory('All'); setSort('featured'); setOwnedOnly(false); setAvailOnly(false); }
  function openMoreInfo(item) {
    setInfoItem(item);
    navigate(`${GENERATORS_ROUTE}/${item.slug}/more-info`);
  }
  function closeMoreInfo() {
    if (moreInfoSlug) navigate(GENERATORS_ROUTE);
    else setInfoItem(null);
  }
  const packs = catalogue.packs || [];
  const sub = account.generator_subscription;

  function toggle(slug) {
    setError('');
    setSelected((current) => (current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug]));
  }

  async function checkout(accessMode, packKey = null) {
    setBusy(true); setError('');
    try {
      const res = await consentCheckout.start(() => API.post('/v1/combat-commerce/generators/checkout', {
        access_mode: accessMode,
        pack_key: packKey,
        selected_slugs: selected,
      }), { purchaseType: 'standalone_generators', intendedSelector: packKey || accessMode });
      // null means the controller is showing the acknowledgement instead of completing.
      if (res && res.data.checkout_url) window.location.assign(res.data.checkout_url);
      else if (res) await refresh();
    } catch (e) {
      const code = e.response?.data?.error;
      setError(code === 'pack_capacity_exceeded'
        ? `That pack holds ${e.response.data.capacity} generators and you have ${e.response.data.selected} selected. Choose a larger pack or remove some.`
        : code === 'plan_mismatch'
          ? 'Your selection changed. The server recalculated the required pack; review the updated summary and confirm again.'
          : code === 'selection_exceeds_maximum_base_pack'
            ? `New subscriptions can start with up to ${e.response.data.maximum_base_capacity} generators. Your loyalty capacity can grow after each completed year.`
          : code || 'Unable to update generator access.');
    } finally { setBusy(false); }
  }

  async function buyExtraChanges(packKey) {
    setBusy(true); setError('');
    try {
      const res = await consentCheckout.start(
        () => API.post('/v1/combat-commerce/generators/extra-changes/checkout', {
          pack_key: packKey,
        }),
        { purchaseType: 'generator_extra_changes', intendedSelector: packKey },
      );
      if (res?.data?.checkout_url) window.location.assign(res.data.checkout_url);
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to prepare the extra-change checkout.');
      setBusy(false);
    }
  }

  async function manageBilling() {
    setBusy(true); setError('');
    try {
      const res = await API.post('/api/payments/create-billing-portal-session', {
        return_url: window.location.href,
      });
      window.location.assign(res.data.url || res.data.portal_url);
    } catch (e) { setError(e.response?.data?.error || 'Unable to open billing management.'); setBusy(false); }
  }

  const currentPlanLabel = !sub ? null
    : sub.access_mode === 'pack'
        ? `${(packs.find((p) => p.key === sub.pack_key) || {}).name || 'Pack'} · ${(sub.selected_slugs || []).length} chosen`
        : `${(sub.selected_slugs || []).length} selected individually`;

  const recommended = quote && quote.recommended;
  const growth = account.generator_growth;
  const canFillSlots = !!(growth && growth.available_slots > 0);
  const canReplace = !!(growth?.reselection?.available || growth?.extra_change_credits > 0);
  const changeLabel = canReplace ? 'CHANGE GENERATORS'
    : canFillSlots ? 'FILL AVAILABLE SLOTS'
      : `CHANGE IN ${growth?.reselection?.days_remaining || 0} DAYS`;
  const activeFilterCount = Number(category !== 'All') + Number(sort !== 'featured')
    + Number(ownedOnly) + Number(availOnly);
  const sortLabel = sort === 'az' ? 'A–Z' : sort === 'category' ? 'Category' : 'Featured';
  const findResults = groupItems.filter((item) => inGroup(item) && inCategory(item) && matches(item) && passesToggles(item));
  const groupVisible = activeGroup
    ? groupItems.filter((item) => inCategory(item) && matches(item) && passesToggles(item))
    : [];
  const groupPrimaryVisible = groupVisible.filter((item) => isPrimaryMember(item, activeGroup && activeGroup.key)).length;
  const groupRelatedVisible = groupVisible.length - groupPrimaryVisible;

  function closeControlModal() { setControlModal(null); }

  function revealGenerator(item) {
    setQuery(item.name);
    setPage(1);
    setControlModal(null);
    setTimeout(() => {
      const selector = `[data-slug="${item.slug}"], [data-featured-slug="${item.slug}"]`;
      document.querySelector(selector)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  return (
    <CombatProShell title={activeGroup ? activeGroup.name : 'D&D & RPG Generators'}
      subtitle={activeGroup ? activeGroup.tagline : 'Independent subscriptions. Subscribe to only the tools you actually use.'}
      navigate={navigate} backTo="/dashboard/gaming" backLabel="Back to Gaming">
      <GuardedCheckoutConsent checkout={consentCheckout} />

      {experienceGroups.length > 0 && (
        <div className="sg-group-row" role="group" aria-label="Generator collections">
          {[{ key: ALL_GROUPS, name: 'All Generators', count: catalogue.count, tagline: 'The complete generator collection' },
            ...experienceGroups].map((row) => {
            const active = (activeGroup ? activeGroup.key : ALL_GROUPS) === row.key;
            const id = groupIdentity(row.key === ALL_GROUPS ? null : row.key);
            return (
              <button key={row.key} type="button" className="sg-group-tab" data-group-tab={row.key}
                aria-pressed={active} onClick={() => selectGroup(row.key)}
                style={{ ...(active ? glassSurface(id.accent2, id.accent, id.bright) : glassSurface('#1F2937', '#475569', '#94A3B8')),
                  ...(active ? { boxShadow: `0 0 26px ${id.glow}` } : null) }}>
                <strong>{row.name}</strong>
                <span>{row.count} {row.count === 1 ? 'generator' : 'generators'}{row.coming_soon_count ? ` · ${row.coming_soon_count} coming soon` : ''}</span>
              </button>
            );
          })}
        </div>
      )}
      {activeGroup && (
        <p className="sg-group-intro" style={{ margin: '0 0 16px', color: 'rgba(237,241,248,.74)', fontSize: 14, lineHeight: 1.55 }}>
          {activeGroup.description}
        </p>
      )}

      <div className="sg-control-row" aria-label="Generator catalogue controls">
        <button ref={findButtonRef} type="button" className="sg-control-launch"
          style={sg.controlLaunch} aria-haspopup="dialog" onClick={() => setControlModal('find')}>
          <strong>FIND GENERATORS</strong>
          <span>{query ? `Search: ${query}` : `${catalogue.count} generators`}</span>
        </button>
        <button ref={filtersButtonRef} type="button" className="sg-control-launch"
          style={sg.controlLaunch} aria-haspopup="dialog" onClick={() => setControlModal('filters')}>
          <strong>CATEGORIES &amp; SORT</strong>
          <span>{activeFilterCount ? `${activeFilterCount} active · ${totalVisible} matches` : `All · ${sortLabel}`}</span>
        </button>
        <button ref={pricingButtonRef} type="button" className="sg-control-launch"
          style={sg.controlLaunch} aria-haspopup="dialog" onClick={() => setControlModal('pricing')}>
          <strong>PRICING &amp; PACKS</strong>
          <span>{money(catalogue.individual_price_cents)} each · {packs.length} packs</span>
        </button>
      </div>

      {/* FEATURED — deeper treatment, never repeated in the grid below. Built for two; renders what
          the catalogue genuinely carries rather than inventing a second product. */}
      {visibleFeatured.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={sg.sectionLabel}>FEATURED GENERATOR SYSTEMS</div>
          <div className={`sg-featured-row${visibleFeatured.length === 1 ? ' sg-featured-one' : ''}`}>
            {visibleFeatured.map((item, index) => {
              const hasAccess = entitled.has(item.slug);
              const isSelected = selected.includes(item.slug);
              // Featured placement does not make a pre-launch product sellable. It keeps the same
              // honest state it has anywhere else: in development, no price, no selection.
              const unreleased = item.available === false;
              return (
                <div
                  key={item.slug}
                  className="sg-featured-card"
                  data-featured-slug={item.slug}
                  data-featured-position={index === 0 ? 'left' : 'right'}
                  data-available={String(!unreleased)}
                  data-artwork={item.artwork_path || ''}
                  style={{ ...cps.card, ...sg.featuredCard, ...(item.artwork_path ? {
                    backgroundImage: `linear-gradient(90deg, rgba(9,11,17,.98) 0%, rgba(9,11,17,.90) 58%, rgba(9,11,17,.32) 100%), url(${item.artwork_path})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                  } : null) }}
                >
                  <div style={sg.featuredEyebrow}>{item.category} · FEATURED</div>
                  <strong style={{ fontSize: 25, lineHeight: 1.15 }}>{item.name}</strong>
                  <span className="sg-f-desc" style={{ color: 'rgba(255,255,255,.72)', fontSize: 15, lineHeight: 1.5 }}>{item.description}</span>
                  <div className="sg-offers">
                    <div style={sg.offersLabel}>WHAT IT OFFERS<span style={sg.offersRule} /></div>
                    <ul style={{ ...sg.offersList, fontSize: 13.5 }}>
                      {(item.capabilities || []).map((point) => <li key={point}>{point}</li>)}
                    </ul>
                  </div>
                  <div style={sg.priceRow}>
                    <span style={{ ...sg.priceLine, ...(unreleased ? sg.statusSoon : hasAccess ? sg.statusOwned : null) }}>
                      {unreleased
                        ? 'In development'
                        : hasAccess
                          ? (account.generator_owner_override ? 'Owned' : 'Included in your subscription')
                          : `${money(catalogue.individual_price_cents)} / month`}
                    </span>
                    <button type="button" className="sg-more-info" onClick={() => openMoreInfo(item)}>More info</button>
                  </div>
                  {unreleased
                    ? <button type="button" className="sg-action" disabled>Coming soon</button>
                    : hasAccess
                      ? <div className="sg-action is-dcc" role="status" aria-label={`${item.name} is available in the Desktop Control Centre`}>Available in DCC</div>
                      : sub
                        ? <button type="button" className="sg-action" disabled={!canFillSlots && !canReplace} onClick={() => setChangeOpen(true)}>
                            {canFillSlots ? 'Available slot' : canReplace ? 'Available to select' : 'Locked until next change'}
                          </button>
                      : <button type="button" className={`sg-action${isSelected ? ' is-selected' : ''}`} onClick={() => toggle(item.slug)}>
                          {isSelected ? 'Selected ✓' : `Select · ${money(catalogue.individual_price_cents)}/mo`}
                        </button>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NORMAL GRID — exactly five per row on desktop, every card the same fixed zones. */}
      <div style={sg.catalogueHead}>
        <div style={{ ...sg.sectionLabel, margin: 0 }}>
          GENERATOR CATALOGUE
          <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'rgba(226,232,240,.55)', marginLeft: 10 }}>
            {activeGroup
              ? `${groupPrimaryVisible} in ${activeGroup.short_name || activeGroup.name}${groupRelatedVisible ? ` · ${groupRelatedVisible} related from other collections` : ''}`
              : `${totalVisible} standard generators${hasActiveFilters ? ' match' : ' shown'} · featured systems above`}
          </span>
        </div>
        {pageCount > 1 && (
          <div style={sg.pager} role="navigation" aria-label="Catalogue pages">
            <button type="button" className="sg-pill" style={sg.pagerBtn} disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)} aria-label="Previous page">{'\u2039'} Prev</button>
            <span style={sg.pagerCount} aria-live="polite">Page {currentPage} of {pageCount}</span>
            <button type="button" className="sg-pill" style={sg.pagerBtn} disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)} aria-label="Next page">Next {'\u203a'}</button>
          </div>
        )}
      </div>
      <div className="sg-grid">
        {pageItems.map((item) => {
          const isSelected = selected.includes(item.slug);
          const hasAccess = entitled.has(item.slug);
          // A pre-launch generator is announced but cannot be bought: no price, no selection.
          const unreleased = item.available === false;
          return (
            <div key={item.slug} className="sg-card" data-slug={item.slug} data-available={String(!unreleased)} data-artwork={item.artwork_path || ''} style={{ ...cps.card, ...sg.card, ...(item.artwork_path ? {
              backgroundImage: `linear-gradient(90deg, rgba(9,11,17,.99) 0%, rgba(9,11,17,.91) 64%, rgba(9,11,17,.43) 100%), url(${item.artwork_path})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
            } : null) }}>
              <span className="sg-category" style={sg.cardCategory}>{item.category}</span>
              <strong className="sg-name" style={sg.cardName}>{item.name}</strong>
              <span className="sg-purpose" style={sg.cardPurpose}>{item.description}</span>
              <div className="sg-offers">
                <div style={sg.offersLabel}>WHAT IT OFFERS<span style={sg.offersRule} /></div>
                <ul style={sg.offersList}>
                  {(item.capabilities || []).map((point) => <li key={point}>{point}</li>)}
                </ul>
              </div>
              <div className="sg-price" style={sg.priceRow}>
                <span style={{ ...sg.priceLine, ...(unreleased ? sg.statusSoon : hasAccess ? sg.statusOwned : null) }}>
                  {unreleased
                    ? 'Not yet available'
                    : hasAccess
                      ? (account.generator_owner_override ? 'Owned' : 'Included in your subscription')
                      : `${money(catalogue.individual_price_cents)} / month`}
                </span>
                <button type="button" className="sg-more-info" onClick={() => openMoreInfo(item)}>More info</button>
              </div>
              {unreleased
                ? <button type="button" className="sg-action" disabled>Coming soon</button>
                : hasAccess
                  ? <div className="sg-action is-dcc" role="status" aria-label={`${item.name} is available in the Desktop Control Centre`}>Available in DCC</div>
                  : sub
                    ? <button type="button" className="sg-action" disabled={!canFillSlots && !canReplace} onClick={() => setChangeOpen(true)}>
                        {canFillSlots ? 'Available slot' : canReplace ? 'Available to select' : 'Locked until next change'}
                      </button>
                  : <button type="button" className={`sg-action${isSelected ? ' is-selected' : ''}`} onClick={() => toggle(item.slug)}>
                      {isSelected ? 'Selected ✓' : `Select · ${money(catalogue.individual_price_cents)}/mo`}
                    </button>}
            </div>
          );
        })}
        {!pageItems.length && (
          <div style={{ ...cps.card, gridColumn: '1 / -1', color: 'rgba(255,255,255,.66)' }}>
            No generators match that search or category.
          </div>
        )}
      </div>

      {pageCount > 1 && (
        <div style={sg.pagerBottom} role="navigation" aria-label="Catalogue pages (bottom)">
          <button type="button" className="sg-pill" style={sg.pagerBtn} disabled={currentPage <= 1}
            onClick={() => { setPage(currentPage - 1); if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label="Previous page">{'\u2039'} Prev</button>
          <span style={sg.pagerCount}>Page {currentPage} of {pageCount}</span>
          <button type="button" className="sg-pill" style={sg.pagerBtn} disabled={currentPage >= pageCount}
            onClick={() => { setPage(currentPage + 1); if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label="Next page">Next {'\u203a'}</button>
        </div>
      )}

      <div style={{ ...cps.card, marginTop: 20, position: 'sticky', bottom: 12, boxShadow: '0 10px 40px #000' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <strong>
              {selected.length}{growth ? ` of ${growth.total_capacity}` : ''} selected
              {!sub && quote ? ` · ${money(quote.individual_price_cents)}/month individually` : ''}
            </strong>
            <div style={{ color: 'rgba(255,255,255,.62)', fontSize: 13 }}>
              {sub && growth
                ? `${growth.available_slots} available slots. ${growth.reselection.available ? 'Generator changes are available now.' : `Next generator change: ${new Date(growth.reselection.next_available_at).toLocaleDateString('en-AU')}.`}`
                : recommended
                ? `${recommended.name} at ${money(recommended.price_cents)}/month${quote.saving_cents > 0 ? `, saving ${money(quote.saving_cents)}` : ''}.`
                : 'Current access remains unchanged until you confirm.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {sub && <button disabled={busy} style={{ ...cps.btn, ...glassSurface('#334155', '#64748B', '#94A3B8') }} onClick={manageBilling}>Manage / cancel</button>}
            {sub && growth && <button type="button" style={{ ...cps.btn, ...glassSurface('#A66D05', '#F59E0B', '#FBBF24') }} onClick={() => setChangeOpen(true)}>{changeLabel}</button>}
            {!sub && <button disabled={busy || !selected.length || !recommended} className="sg-selection-cta"
              style={{ ...cps.btn, ...glassSurface('#A66D05', '#F59E0B', '#FBBF24'), minWidth: 280, padding: '12px 28px', opacity: !selected.length || !recommended ? .45 : 1 }}
              onClick={() => recommended && checkout(recommended.mode, recommended.pack_key)}>
              {recommended ? `Confirm ${recommended.name} · ${money(recommended.price_cents)}/mo` : 'Select generators to continue'}
            </button>}
          </div>
        </div>
        {error && <div style={{ color: '#fca5a5', marginTop: 10 }}>{error}</div>}
      </div>

      {controlModal === 'find' && (
        <GeneratorControlDialog id="sg-find" title="Find Generators" onClose={closeControlModal}
          returnFocusRef={findButtonRef} initialFocusRef={searchInputRef}
          footer={<>
            {query && <button type="button" className="sg-pill" style={sg.pillSm} onClick={() => setQuery('')}>Clear search</button>}
            <button type="button" className="sg-pill" style={{ ...sg.pillSm, ...sg.pillActive }} onClick={closeControlModal}>Done</button>
          </>}>
          <p style={{ margin: '0 0 14px', color: 'rgba(237,241,248,.70)', lineHeight: 1.55 }}>
            Search generator names, purposes, capabilities and categories.
          </p>
          <input ref={searchInputRef} className="sg-search" type="search" value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search generators by name, purpose, capability or category"
            aria-label="Search generators" style={sg.search} />
          <div role="status" style={{ marginTop: 12, color: '#c4b5fd', fontSize: 13, fontWeight: 800 }}>
            {findResults.length} {findResults.length === 1 ? 'generator matches' : 'generators match'}
          </div>
          <div className="sg-control-results" aria-label="Matching generators">
            {findResults.slice(0, 40).map((item) => (
              <button key={item.slug} type="button" className="sg-control-result"
                data-search-result={item.slug} onClick={() => revealGenerator(item)}>
                <span><strong>{item.name}</strong><small>{item.category}</small></span>
                <span aria-hidden="true">Show →</span>
              </button>
            ))}
          </div>
          {findResults.length > 40 && (
            <p style={{ color: 'rgba(237,241,248,.58)', fontSize: 12.5 }}>
              Showing the first 40 matches. Refine the search to reach a specific generator.
            </p>
          )}
          {!findResults.length && <p style={{ color: 'rgba(237,241,248,.64)' }}>No generators match that search.</p>}
        </GeneratorControlDialog>
      )}

      {controlModal === 'filters' && (
        <GeneratorControlDialog id="sg-filters" title="Categories & Sort" onClose={closeControlModal}
          returnFocusRef={filtersButtonRef}
          footer={<>
            {hasActiveFilters && <button type="button" className="sg-pill" style={sg.pillSm} onClick={clearAll}>Clear filters</button>}
            <button type="button" className="sg-pill" style={{ ...sg.pillSm, ...sg.pillActive }} onClick={closeControlModal}>Done</button>
          </>}>
          <div className="sg-filter-workspace-grid">
            <section className="sg-control-section">
              <h3>Category</h3>
              <div role="group" aria-label="Filter by category" style={sg.modalPills}>
                {categories.map((name) => {
                  const active = category === name;
                  return (
                    <button key={name} type="button" className="sg-pill" aria-pressed={active}
                      onClick={() => setCategory(name)} style={{ ...sg.pill, ...(active ? sg.pillActive : null) }}>
                      {name}
                    </button>
                  );
                })}
              </div>
            </section>
            <section className="sg-control-section">
              <h3>Sort</h3>
              <div role="group" aria-label="Sort generators" style={sg.modalPills}>
                {[['featured', 'Featured'], ['az', 'A–Z'], ['category', 'Category']].map(([key, label]) => (
                  <button key={key} type="button" className="sg-pill" aria-pressed={sort === key}
                    onClick={() => setSort(key)} style={{ ...sg.pill, ...(sort === key ? sg.pillActive : null) }}>
                    {label}
                  </button>
                ))}
              </div>
            </section>
            <section className="sg-control-section">
              <h3>Catalogue filters</h3>
              <div role="group" aria-label="Filter generators" style={sg.modalPills}>
                <button type="button" className="sg-pill" aria-pressed={ownedOnly}
                  onClick={() => setOwnedOnly((value) => !value)} style={{ ...sg.pill, ...(ownedOnly ? sg.pillActive : null) }}>
                  Owned
                </button>
                <button type="button" className="sg-pill" aria-pressed={availOnly}
                  onClick={() => setAvailOnly((value) => !value)} style={{ ...sg.pill, ...(availOnly ? sg.pillActive : null) }}>
                  Available now
                </button>
              </div>
            </section>
          </div>
          <div className="sg-filter-match-count" role="status" style={{ color: '#c4b5fd', fontSize: 13, fontWeight: 800 }}>
            {totalVisible} {totalVisible === 1 ? 'generator matches' : 'generators match'} the current view
          </div>
        </GeneratorControlDialog>
      )}

      {controlModal === 'pricing' && (
        <GeneratorControlDialog id="sg-pricing" title="Pricing & Packs" onClose={closeControlModal}
          returnFocusRef={pricingButtonRef}>
          <section className="sg-control-section">
            <h3>Choose exactly what you use</h3>
            <p style={{ color: 'rgba(255,255,255,.72)', lineHeight: 1.6, marginTop: 0 }}>
              {catalogue.count} standalone generators at {money(catalogue.individual_price_cents)} each per month.
              Packs are <strong>choose-any</strong>: pick which generators fill the slots and change them later.
              Keep an active subscription and your monthly price stays fixed. Every completed year adds
              two generator slots, up to 200 total. Categories are navigation only, not packs or separate products.
            </p>
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', color: 'rgba(255,255,255,.62)', fontSize: 12 }}>
              <span>Choose the generators you need</span><span>·</span>
              <span>Change as your campaign changes every 90 days</span><span>·</span>
              <span>Your price stays grandfathered</span><span>·</span>
              <span>Your plan grows by 2 slots each completed year</span>
            </div>
            <button type="button" style={{ ...cps.btn, ...glassSurface('#312E81', '#7C3AED', '#A78BFA'), marginTop: 16 }}
              onClick={() => navigate('/dashboard/generator-growth-pricing')}>
              Explore Generator Growth &amp; Pricing
            </button>
          </section>

          {sub && (
            <section className="sg-control-section">
              <h3>Current plan</h3>
              <div style={{ color: '#c4b5fd', fontSize: 14 }}>
                <strong>{currentPlanLabel}</strong>
                {' · '}{sub.status}
                {growth ? ` · ${growth.total_capacity} total slots (${growth.loyalty_bonus_capacity} loyalty)` : ''}
                {growth?.locked_monthly_price_cents != null ? ` · ${money(growth.locked_monthly_price_cents)}/month locked` : ''}
                {sub.cancel_at_period_end ? ' · cancels at period end' : ''}
                {sub.current_period_end ? ` · period ends ${new Date(sub.current_period_end).toLocaleDateString()}` : ''}
              </div>
              {growth && <button type="button" className="sg-pill" onClick={() => { setControlModal(null); setChangeOpen(true); }}
                style={{ ...sg.pill, ...sg.pillActive, marginTop: 13 }}>{changeLabel}</button>}
              {growth && <div style={{ color: '#fcd34d', fontSize: 13, marginTop: 10 }}>
                Extra generator changes available: <strong>{growth.extra_change_credits || 0}</strong>
                {!growth.reselection?.available && growth.extra_change_credits > 0
                  ? ' · Your next confirmed replacement will use one credit and keep the free-change date unchanged.'
                  : ''}
              </div>}
            </section>
          )}

          <section className="sg-control-section">
            <h3>Monthly pricing</h3>
            <div className="sg-pricing-grid">
              {[
                { key: 'individual', name: '1 generator', capacity: 1, price_cents: catalogue.individual_price_cents },
                ...packs,
              ].map((tier) => {
                const each = Math.round(tier.price_cents / tier.capacity);
                const saving = tier.capacity * catalogue.individual_price_cents - tier.price_cents;
                const isRecommended = recommended
                  && ((recommended.mode === 'pack' && recommended.pack_key === tier.key)
                    || (recommended.mode === 'individual' && tier.key === 'individual'));
                return (
                  <div key={tier.key} style={{
                    background: 'rgba(255,255,255,.04)', borderRadius: 12, padding: '12px 14px',
                    border: isRecommended ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,.1)',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'rgba(255,255,255,.66)' }}>{tier.name}</div>
                    <div style={{ fontSize: 21, fontWeight: 800 }}>{money(tier.price_cents)}<span style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>/mo</span></div>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>
                      {tier.capacity > 1
                        ? `${money(each)} each${saving > 0 ? ` · save ${money(saving)}` : ''}`
                        : 'per generator'}
                    </div>
                    {isRecommended && <div style={{ fontSize: 10, fontWeight: 800, color: '#f59e0b', marginTop: 4 }}>BEST FOR YOUR SELECTION</div>}
                  </div>
                );
              })}
            </div>
          </section>

          {sub && (catalogue.extra_changes || []).length > 0 && (
            <section className="sg-control-section">
              <h3>Extra generator changes</h3>
              <p style={{ color: 'rgba(255,255,255,.65)', lineHeight: 1.55, marginTop: 0 }}>
                Optional one-time flexibility. One credit covers one whole confirmed replacement event,
                no matter how many generators you replace. Free changes are always used first.
              </p>
              <div className="sg-purchase-grid">
                {catalogue.extra_changes.map((pack) => (
                  <article key={pack.key} style={sg.extraCard}>
                    <strong style={{ fontSize: 17 }}>{pack.name}</strong>
                    <span style={{ fontSize: 25, fontWeight: 900 }}>{money(pack.price_cents)}</span>
                    <span style={{ color: 'rgba(255,255,255,.58)', fontSize: 12 }}>
                      {money(pack.per_change_cents)} per change
                      {pack.saving_cents > 0 ? ' · save ' + money(pack.saving_cents) : ''}
                    </span>
                    <button type="button" disabled={busy} className="sg-action" onClick={() => buyExtraChanges(pack.key)}>
                      Buy {pack.credits === 1 ? 'change' : pack.credits + ' changes'}
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}
        </GeneratorControlDialog>
      )}

      {changeOpen && sub && growth && <GeneratorReselectionModal
        catalogue={catalogue} subscription={sub} growth={growth}
        onClose={() => setChangeOpen(false)} onSaved={refresh}
      />}
      {infoItem && <ProductInformationDialog item={infoItem} priceLabel={`${money(catalogue.individual_price_cents)} / month`} onClose={closeMoreInfo} />}
    </CombatProShell>
  );
}

/**
 * Presentation for the catalogue surface. The fixed zone heights live in SG_CSS (they need media
 * queries and line clamps); these are the colours, weights and spacing that sit inside them.
 */
const sg = {
  controlLaunch: {
    ...glassSurface('#312E81', '#7C3AED', '#A78BFA'),
    width: '100%', boxSizing: 'border-box',
  },
  search: {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.14)',
    borderRadius: 12, padding: '13px 16px', color: '#fff', fontSize: 15,
  },
  pill: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    borderRadius: 999, padding: '8px 16px',
    fontSize: 12.5, fontWeight: 800, letterSpacing: '.04em', cursor: 'pointer',
  },
  pillActive: { ...glassSurface('#A66D05', '#F59E0B', '#FBBF24') },
  extraCard: {
    display: 'flex', flexDirection: 'column', gap: 8, minHeight: 145,
    padding: 16, borderRadius: 12, background: 'rgba(245,158,11,.08)',
    border: '1px solid rgba(245,158,11,.3)',
  },
  pillSm: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    borderRadius: 999, padding: '6px 13px',
    fontSize: 11.5, fontWeight: 800, letterSpacing: '.04em', cursor: 'pointer',
  },
  modalPills: { display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' },
  catalogueHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, flexWrap: 'wrap', margin: '0 0 12px',
  },
  pager: { display: 'flex', alignItems: 'center', gap: 10 },
  pagerBottom: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  pagerBtn: { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 999, padding: '7px 14px', fontSize: 12, cursor: 'pointer' },
  pagerCount: { fontSize: 12.5, fontWeight: 800, color: 'rgba(226,232,240,.8)', letterSpacing: '.02em', whiteSpace: 'nowrap' },

  sectionLabel: {
    fontSize: 12, fontWeight: 800, letterSpacing: '.12em',
    color: 'rgba(255,255,255,.62)', margin: '0 0 12px',
  },

  featuredCard: {
    display: 'grid',
    gridTemplateRows: '16px 34px 44px 1fr 20px 42px',
    gap: 11,
    padding: 20,
    height: 382,   // measured: the smallest height that fits the deeper featured content
    boxSizing: 'border-box',
  },
  featuredEyebrow: { fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: '#f59e0b' },

  /* Overrides cps.card's INLINE display:flex/gap. A class cannot beat an inline style, which is why
     the zone grid previously did nothing and the cards showed dead space under the action. Heights
     are the bounded content: one line of category, two of title, two of purpose, the OFFERS block,
     one status line, the action. Nothing is padded out to reach an arbitrary total. */
  card: {
    display: 'grid',
    gridTemplateRows: '14px 44px 40px 1fr 18px 38px',
    gap: 8,
    padding: 16,
    height: 345,   // measured: the smallest height that fits all six zones with nothing clipped
    boxSizing: 'border-box',
  },
  cardCategory: { color: 'rgba(167,139,250,0.92)', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' },
  cardName: { fontSize: 16.5, fontWeight: 800, color: '#FFFFFF', lineHeight: 1.32, letterSpacing: '-0.005em' },
  cardPurpose: { color: 'rgba(226,232,240,0.66)', fontSize: 12.5, lineHeight: 1.55 },
  offersLabel: {
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 9.5, fontWeight: 800, letterSpacing: '.16em',
    color: 'rgba(226,232,240,0.58)', marginBottom: 8,
  },
  offersRule: { flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(167,139,250,.42), transparent)' },
  offersList: { color: 'rgba(237,241,248,0.80)', fontSize: 12, lineHeight: 1.62 },
  priceLine: { fontSize: 11.5, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'rgba(226,232,240,0.72)' },
  priceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minWidth: 0 },
  statusOwned: { color: '#5FE3B0' },
  statusSoon: { color: '#9AA6BA' },
  cardBtn: { width: '100%', padding: '10px 12px', fontSize: 13 },
  cardBtnSoon: { opacity: .5, cursor: 'default', background: 'rgba(255,255,255,.06)' },
};
